/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

import axios from 'axios'
import { API_COLLECTOR_URL } from '~/config'

export type ValidatorStats = {
  voteId: string
  validatorId: string
  fee: number
  apy: number
  apyEst: number
  totalStake: number
  network: string
  name: string | undefined
  details: string | undefined
  website: string | undefined
  keybaseUsername: string | undefined
  iconUrl: string | undefined
  inTop33: boolean
  inKiwi: boolean
  isDelinquent: boolean
  lastVote: string
  svName: string
  apyComparedMax: number
  vaScore: number
}

export type GetValidatorHistoryProps = {
  voterKey: string
  network: string
  epochFrom?: number
  epochCount?: number
}

export type ValidatorHistory = {
  apy: number
  epoch: number
  fee: number
  totalStake: number
  commissionReward: number | string
  votingReward: number | string
  votingFee: number | string
  votingCompensation: number | string
  jitoReward: number | string
  skippedSlots: number
  leaderSlotsDone: number
  leaderSlotsTotal: number
}

export type ApyStats = {
  apy: number
  epoch: number
}

type CoinStats = {
  ath: number
  ath_change_percentage: number
  ath_date: string
  atl: number
  atl_change_percentage: number
  atl_date: string
  circulating_supply: number
  current_price: number
  fully_diluted_valuation: null
  high_24h: number
  id: string
  image: string
  last_updated: string
  low_24h: number
  market_cap: number
  market_cap_change_24h: number
  market_cap_change_percentage_24h: number
  market_cap_rank: number
  // max_supply: null,
  name: string
  price_change_24h: number
  price_change_percentage_24h: number
  price_change_percentage_24h_in_currency: number
  // roi: null,
  symbol: string
  total_supply: number
  total_volume: number
}

export type CoinHistory = {
  name: string
  close: number
  epoch: number
}

export type EpochData = {
  id: number
  epoch: number
  network: string
  firstSlot: number
  lastSlot: number
  firstBlock: number
  lastBlock: number | null
  startTime: string
  endTime: string
  createdAt: string
}

export async function getValidatorsStats(network: string) {
  return new Promise<Array<ValidatorStats>>((resolve, reject) => {
    // fetch(`http://localhost:3000/validators/list?network=${network}`)
    fetch(`${API_COLLECTOR_URL}/validators/list?network=${network}`)
      .then(res => res.json())
      .then(
        (res) => {
          if (res.data?.length > 0) {
            resolve(res.data)
          } else {
            resolve([])
          }
        },
        (error) => {
          reject(error)
          console.error(error)
        },
      )
  })
}

export async function getValidatorHistory({ voterKey, network, epochFrom, epochCount = 30 }: GetValidatorHistoryProps) {
  return new Promise<Array<ValidatorHistory>>((resolve, _reject) => {
    fetch(`${API_COLLECTOR_URL}/validators-history/history?vote_id=${voterKey}&network=${network}&epoch_count=${epochCount}${epochFrom ? `&epoch_from=${epochFrom}` : ''}`)
      .then(res => res.json())
      .then(
        (res) => {
          if (res.data?.length > 0) {
            resolve(res.data)
          }
        },
        (error) => {
          console.error(error)
        },
      )
  })
}

export async function getApyHistory(voterKey: string) {
  return new Promise<Array<ApyStats>>((resolve, _reject) => {
    fetch(`${API_COLLECTOR_URL}/apy/history?vote_id=${voterKey}`)
      .then(res => res.json())
      .then(
        (res) => {
          if (res.data?.length > 0) {
            resolve(res.data)
          } else {
            // resolve([]);
            // reject(Error('Promise rejected'));
          }
        },
        (error) => {
          console.error(error)
        },
      )
  })
}

export async function getAverageApy() {
  return new Promise<Array<ApyStats>>((resolve, _reject) => {
    fetch(`${API_COLLECTOR_URL}/apy-average/history`)
      .then(res => res.json())
      .then(
        (res) => {
          if (res.data?.length > 0) {
            resolve(res.data)
          } else {
            // resolve([]);
            // reject(Error('Promise rejected'));
          }
        },
        (error) => {
          console.error(error)
        },
      )
  })
}

export async function getCoinStats(id = 'solana'): Promise<CoinStats> {
  const res = await fetch(`${API_COLLECTOR_URL}/coins/${id}`)
  return (await res.json()).data
}

export async function getCoinHistory({ epoch, id = 'solana', network }: { epoch: number | string, id?: string, network?: string }): Promise<CoinHistory[]> {
  const res = await fetch(`${API_COLLECTOR_URL}/coins-history/${id}?epoch=${epoch}${network ? `&network=${network}` : ''}`)
  return (await res.json()).data
}

export async function getCoinPriceByTime(time: number, id = 'solana'): Promise<CoinHistory> {
  const res = await fetch(`${API_COLLECTOR_URL}/coins-history/${id}/${time}`)
  return (await res.json()).data
}

export async function checkWalletInDelegators(wallet: string) {
  const res = await fetch(`${API_COLLECTOR_URL}/delegator-wallets/${wallet}`)
  return await res.json()
}

export async function addToDelegators(wallet: string, network?: string) {
  const resp = await axios.post(
    `${API_COLLECTOR_URL}/delegator-wallets/${wallet}${network ? `?network=${network}` : ''}`,
  )
  return resp.data
}

export async function getWalletRewards(network: string, wallet: string) {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/stake-accounts/list?wallet=${wallet}&network=${network}`,
  )
  return resp.data
}

export async function getStakesStatus(network: string, wallet: string) {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/stake-accounts/checkCompleteness?wallet=${wallet}&network=${network}`,
  )
  return resp.data
}

export async function getEpochByTime(time: number, network = 'mainnet') {
  return new Promise<EpochData>((resolve, reject) => {
    fetch(`${API_COLLECTOR_URL}/epochs/epoch-by-timestamp/${time}/${network}`)
      .then(res => res.json())
      .then(
        (res) => {
          resolve(res.data)
        },
        (error) => {
          reject(error)
          console.error(error)
        },
      )
  })
}

export async function getEpochs(epochs: string, network = 'mainnet') {
  return new Promise<EpochData[]>((resolve, reject) => {
    fetch(`${API_COLLECTOR_URL}/epochs/${network}?epochs=${epochs}`)
      .then(res => res.json())
      .then(
        (res) => {
          resolve(res.data)
        },
        (error) => {
          reject(error)
          console.error(error)
        },
      )
  })
}

export type EpochInfo = { 
  absoluteSlot: number
  blockHeight: number
  epoch: number
  slotIndex: number
  slotsInEpoch: number
  transactionCount: number
  tps: number
  realHourlySlotTime: number
  network: string
  hourlySlotTime: number
}

export async function getEpochInfo(network = 'mainnet'): Promise<EpochInfo> {
  const resp = await axios.get(
    `${API_COLLECTOR_URL}/current-epochs/${network}`,
  )
  return resp.data.data
}
